/**
 * Client-side image compression for upload.
 *
 * Phone-camera photos of handwritten timesheets are typically 4-12 MB at full
 * resolution — far more than necessary for OCR accuracy. We downscale to
 * `MAX_DIMENSION` on the longest side and re-encode as JPEG at `QUALITY`,
 * which typically shrinks 6 MB → ~700 KB without hurting GPT vision accuracy.
 *
 * iPhone HEIC images are transcoded to JPEG first via `heic2any`, since
 * Chrome/Firefox cannot draw HEIC to a canvas. PDFs and small files pass
 * through untouched.
 */

const MAX_DIMENSION = 2000; // px — generous for handwriting OCR (≥ 200 DPI on letter paper)
const QUALITY = 0.85; // JPEG quality
const SKIP_BELOW_BYTES = 1.5 * 1024 * 1024; // don't bother for files already under 1.5 MB

const COMPRESSIBLE_TYPES = new Set([
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
]);
const HEIC_TYPES = new Set(['image/heic', 'image/heif']);

const isHeicByName = (name = '') => /\.(heic|heif)$/i.test(name);

/**
 * Transcode a HEIC/HEIF blob to a JPEG blob via the heic2any worker.
 * Lazy-imported so the ~150 KB library only loads when an iPhone photo is
 * actually picked.
 */
async function transcodeHeicToJpeg(file) {
  const { default: heic2any } = await import('heic2any');
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: QUALITY });
  // heic2any returns either a Blob (single page) or Blob[] (multi-page HEIC) — pick first
  const jpegBlob = Array.isArray(blob) ? blob[0] : blob;
  const baseName = file.name.replace(/\.(heic|heif)$/i, '') || 'photo';
  return new File([jpegBlob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))), type, quality);
  });

/**
 * Returns a (possibly new) File object suitable for upload.
 * Always resolves — never throws — so a failed compression falls back
 * to the original file.
 */
export async function compressImageForUpload(file) {
  if (!file) return file;

  // HEIC needs a transcode pass first since Chrome/Firefox can't draw it to a
  // canvas. Detect by mime type AND filename — Safari sometimes leaves type
  // empty when files come from the camera roll.
  let working = file;
  if (HEIC_TYPES.has(file.type) || isHeicByName(file.name)) {
    try {
      working = await transcodeHeicToJpeg(file);
    } catch {
      // If transcoding failed (e.g. unsupported HEVC variant or browser env
      // missing the codec), tag the original file so the caller can decide
      // whether to skip it. We cannot ship a HEIC to the backend — the AI
      // vision provider only accepts JPEG/PNG/WebP.
      const tagged = file;
      try {
        Object.defineProperty(tagged, '__heicTranscodeFailed', { value: true });
      } catch (defErr) {
        // File object was frozen/sealed (exotic case). The caller can still
        // detect failure via its own HEIC check; we just lose the tag.
        console.warn('[imageCompression] could not tag HEIC failure on file:', defErr?.message);
      }
      return tagged;
    }
  }

  if (!COMPRESSIBLE_TYPES.has(working.type)) return working;
  if (working.size < SKIP_BELOW_BYTES) return working;

  try {
    const img = await loadImageFromFile(working);
    const longest = Math.max(img.width, img.height);
    const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
    const targetW = Math.round(img.width * scale);
    const targetH = Math.round(img.height * scale);

    // If neither dimension change nor recompression would meaningfully shrink
    // the file (e.g. already a tightly-encoded ~2 MB JPEG), bail.
    if (scale === 1 && working.size < 3 * 1024 * 1024) return working;

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    // White background so PNGs with transparency don't render black on JPEG conversion
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await canvasToBlob(canvas, 'image/jpeg', QUALITY);
    if (!blob || blob.size >= working.size) return working; // no improvement, keep current

    // Preserve original filename, swap extension to .jpg
    const baseName = working.name.replace(/\.(jpe?g|png|webp|heic|heif)$/i, '') || 'upload';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    // Compression isn't worth blocking the upload — return whatever we have.
    return working;
  }
}

/**
 * Apply compressImageForUpload to each file in an array.
 * Returns a new array of (possibly compressed) Files in the same order.
 */
export async function compressImagesForUpload(files) {
  return Promise.all(Array.from(files).map(compressImageForUpload));
}
